import React, { useRef, useEffect } from "react";
import cn from "classnames";
import { isIE } from "react-device-detect";
import Button from "components/ui/Button";
import useConfig from "hooks/useConfig";
import useLayout from "hooks/useLayout";
import useModal from "hooks/useModal";
import { positionTakeoverModal, clearTakeoverModal, setBodyScroll } from "helpers/modal";
import "styles/ui/global-modal.scss";

const GlobalModal = ({ buttonText, className, setModalHeight, height }) => {
  const baseRef = useRef(null);
  const modalBodyRef = useRef(null);
  const modalBackdropRef = useRef(null);
  const { enableModalHeightResize } = useConfig();
  const { closeModal, content, modal, options } = useModal();
  const { isTablet, isMobile, isDesktop, width: viewportWidth, height: viewportHeight } = useLayout();
  const enableFixedHeightOption = options.fixedHeight && isMobile;
  const enableTakeoverOption = (options.takeover && isDesktop) || (options.takeoverTablet && !isTablet);

  // Close modal with escape
  useEffect(() => {
    const handleKeys = e => {
      if (e.keyCode === 27 && modal) {
        closeModal();
      }
    };

    document.addEventListener("keyup", handleKeys);
    return () => {
      document.removeEventListener("keyup", handleKeys);
    };
  }, [closeModal, modal]);

  // Autofocus
  useEffect(() => {
      if (modal && modalBodyRef?.current && options.autoFocus && !isIE) {
        modalBodyRef.current.focus();
    }
  }, [modal, options.autoFocus]);

  // Takeover
  useEffect(() => {
    modal ? (
      enableTakeoverOption
        ? positionTakeoverModal(baseRef, modalBackdropRef, modalBodyRef, viewportWidth, viewportHeight)
        : clearTakeoverModal(modalBodyRef)
    ) : setBodyScroll(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modal, enableTakeoverOption, viewportWidth, viewportHeight]);

  // Modal height resize
  useEffect(() => {
    if (enableModalHeightResize) {
      modal
        ? modalBodyRef.current?.offsetHeight > height && setModalHeight(modalBodyRef.current?.offsetHeight)
        : setModalHeight(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modal]);

  const handleButtonClick = e => {
    e.preventDefault();
    closeModal();
  };

  const handleOverlayClick = e => {
    e.stopPropagation();
  };

  if (!modal) {
    return null;
  }

  const baseClass = "otrl-jp__global-modal";
  const classes = cn(baseClass, {
    [`${baseClass}--closed`]: !modal,
    [`${baseClass}--fixed-height`]: enableFixedHeightOption,
    [`${baseClass}--takeover`]: enableTakeoverOption,
  }, className);

  const bodyClasses = cn(`${baseClass}__body`, {
    [options.classNames?.bodyClassName]: options.classNames?.bodyClassName
  });

  return (
    <div
      className={classes}
      onClick={handleOverlayClick}
      ref={baseRef}
    >
      {enableTakeoverOption && (
        <div className={`${baseClass}__backdrop`} ref={modalBackdropRef} onClick={closeModal}></div>
      )}
      <div className={bodyClasses} ref={modalBodyRef} tabIndex="-1">
        {content}
        {buttonText && (
          <Button
            className="otrl-jp__button--full-width"
            text={buttonText}
            onClick={handleButtonClick}
          />
        )}
      </div>
    </div>
  );
};

export default GlobalModal;
